export interface ErrorObject {
  invalid: boolean;
  message: string;
}

const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const passwordPattern =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/;

const checkRequired = <T>(
  data: T,
  customMessages: Partial<Record<string, string>>
): ErrorObject | null => {
  if (!data) {
    return {
      invalid: true,
      message: customMessages.required ?? 'This field is required.',
    };
  }
  return null;
};

const checkEmail = (
  data: string,
  customMessages: Partial<Record<string, string>>
): ErrorObject | null => {
  if (!emailPattern.test(data)) {
    return {
      invalid: true,
      message:
        customMessages.email ?? 'This field must be a valid email address.',
    };
  }
  return null;
};

const checkPassword = (
  data: string,
  customMessages: Partial<Record<string, string>>
): ErrorObject | null => {
  if (!passwordPattern.test(data)) {
    return {
      invalid: true,
      message:
        customMessages.password ??
        'Password must contain at least 8 characters, with at least one uppercase letter, one lowercase letter, and one digit.',
    };
  }
  return null;
};

const checkMinLength = (
  data: string,
  minLength: number,
  customMessages: Partial<Record<string, string>>
): ErrorObject | null => {
  if (data.length < minLength) {
    return {
      invalid: true,
      message:
        customMessages.minLength ??
        `This field must be at least ${minLength} characters long.`,
    };
  }
  return null;
};

const checkMaxLength = (
  data: string,
  maxLength: number,
  customMessages: Partial<Record<string, string>>
): ErrorObject | null => {
  if (data.length > maxLength) {
    return {
      invalid: true,
      message:
        customMessages.maxLength ??
        `This field must be no more than ${maxLength} characters long.`,
    };
  }
  return null;
};

const checkMinSelected = <T>(
  data: T,
  customMessages: Partial<Record<string, string>>
): ErrorObject | null => {
  if (Array.isArray(data) && data.length < 1) {
    return {
      invalid: true,
      message:
        customMessages.minSelected ?? 'You must select at least one option.',
    };
  }
  return null;
};

const runValidationRule = <T>(
  rule: string,
  data: T,
  minLength: number,
  maxLength: number,
  customMessages: Partial<Record<string, string>>
): ErrorObject | null => {
  switch (rule) {
  case 'required':
    return checkRequired(data, customMessages);
  case 'email':
    if (typeof data === 'string') return checkEmail(data, customMessages);
    break;
  case 'password':
    if (typeof data === 'string') return checkPassword(data, customMessages);
    break;
  case 'minLength':
    if (typeof data === 'string')
      return checkMinLength(data, minLength, customMessages);
    break;
  case 'maxLength':
    if (typeof data === 'string')
      return checkMaxLength(data, maxLength, customMessages);
    break;
  case 'minSelected':
    return checkMinSelected(data, customMessages);
  }
  return null;
};

export const validateData = <T = string>(
  data: T,
  rules: string[],
  minLength = 0,
  maxLength = 999,
  customMessages: Partial<Record<string, string>> = {}
): ErrorObject => {
  const errors: ErrorObject = {
    invalid: false,
    message: '',
  };

  for (const rule of rules) {
    const error = runValidationRule(
      rule,
      data,
      minLength,
      maxLength,
      customMessages
    );
    if (error) {
      return error;
    }
  }

  return errors;
};
